/**
 * TerminalFloorNav
 * Cross-link strip at the bottom of every terminal dashboard so managers can
 * jump between Marina → Argus → Treasury → Staking → Governance → Platform
 * without going through the admin sidebar. Makes the terminal surface feel
 * like one unified trading floor.
 *
 * Now with LIVE metrics per desk, polled every 30s from /api/floor-nav/metrics.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Activity, Eye, Wallet, Lock, Vote, Shield, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';

const DESKS = [
  { key: 'marina',     path: '/marina',         label: 'Marina',     sub: 'operations', icon: Activity, code: 'OPS' },
  { key: 'argus',      path: '/argus',          label: 'Argus',      sub: 'audit',      icon: Eye,      code: 'AUD' },
  { key: 'treasury',   path: '/treasury',       label: 'Treasury',   sub: 'capital',    icon: Wallet,   code: 'CAP' },
  { key: 'staking',    path: '/staking',        label: 'Staking',    sub: 'yield',      icon: Lock,     code: 'STK' },
  { key: 'governance', path: '/governance',     label: 'Governance', sub: 'votes',      icon: Vote,     code: 'GOV' },
  { key: 'platform',   path: '/platform-admin', label: 'Platform',   sub: 'root',       icon: Shield,   code: 'ROOT' },
];

export default function TerminalFloorNav() {
  const { pathname } = useLocation();
  const [metrics, setMetrics] = useState(null);
  const [flashKey, setFlashKey] = useState(null);
  // Track which desks we've already paged in this browser session to avoid spam.
  // Persisted in sessionStorage so it survives navigation between desks.
  const pagedRef = useRef(null);
  if (pagedRef.current === null) {
    try { pagedRef.current = new Set(JSON.parse(sessionStorage.getItem('fn_paged') || '[]')); }
    catch { pagedRef.current = new Set(); }
  }

  useEffect(() => {
    let mounted = true;
    const load = () => {
      fetch(`${BACKEND_URL}/api/floor-nav/metrics`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (!mounted || !d) return;
          setFlashKey(d.ts);
          setMetrics(d);

          // ── Cross-desk alert paging ──────────────────────────────
          // If another desk (not the one we're on) is at alert level,
          // and we haven't paged for it this session, pop a toast.
          Object.entries(d.desks || {}).forEach(([key, m]) => {
            if (!m?.attention || m.attention.level !== 'alert') return;
            const desk = DESKS.find((x) => x.key === key);
            if (!desk) return;
            const onThisDesk = pathname === desk.path || pathname.startsWith(desk.path + '/');
            if (onThisDesk) return;
            if (pagedRef.current.has(key)) return;

            const Icon = desk.icon;
            toast.custom(
              () => (
                <div
                  className="terminal-theme flex items-start gap-3 px-4 py-3 bg-[color:var(--t-bg-2)] border border-[color:var(--t-amber)]/60 min-w-[280px]"
                  style={{ boxShadow: '0 8px 24px rgba(0,0,0,0.6), inset 0 0 24px rgba(255,176,0,0.06)' }}
                  data-testid={`floor-nav-toast-${key}`}
                >
                  <div className="w-8 h-8 border border-[color:var(--t-amber)]/40 flex items-center justify-center shrink-0">
                    <Icon className="w-3.5 h-3.5 text-[color:var(--t-amber)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[10px] tracking-[0.22em] text-[color:var(--t-amber)] uppercase flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--t-amber)] animate-pulse" />
                      PAGE &middot; {desk.code}
                    </div>
                    <div className="mt-1 text-[13px] text-[color:var(--t-text)]">
                      <span className="terminal-font font-bold">{desk.label}</span>
                      <span className="text-[color:var(--t-text-3)]"> &mdash; </span>
                      <span className="text-[color:var(--t-text-2)]">{m.attention.message}</span>
                    </div>
                    <a
                      href={desk.path}
                      className="mt-2 font-mono text-[10px] text-[color:var(--t-amber)] hover:underline inline-flex items-center gap-1"
                    >
                      open_{desk.code.toLowerCase()}() <ArrowRight className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              ),
              { duration: 6000 }
            );

            pagedRef.current.add(key);
            try { sessionStorage.setItem('fn_paged', JSON.stringify([...pagedRef.current])); }
            catch (_e) { /* ignore */ }
          });
        })
        .catch(() => {});
    };
    load();
    const iv = setInterval(load, 30000);
    return () => { mounted = false; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const desks = metrics?.desks || {};

  return (
    <div
      className="terminal-theme border-t border-[color:var(--t-border)] bg-[color:var(--t-bg)] mt-12"
      data-testid="terminal-floor-nav"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-10 py-7">
        <div className="flex items-center gap-3 mb-5 font-mono text-[10px] uppercase tracking-[0.22em] text-[color:var(--t-amber)]">
          <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--t-amber)] animate-pulse" />
          <span>floor_nav</span>
          <span className="text-[color:var(--t-text-3)] normal-case tracking-normal text-[11px]">
            &middot; the trading floor
          </span>
          {metrics && (
            <span className="ml-auto text-[color:var(--t-text-3)] normal-case tracking-normal text-[10px]">
              sampled 30s
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-[color:var(--t-border)] border border-[color:var(--t-border)]">
          {DESKS.map((d) => {
            const active = pathname === d.path || pathname.startsWith(d.path + '/');
            const Icon = d.icon;
            const metric = desks[d.key];
            const attention = metric?.attention;
            const isAlert = attention?.level === 'alert';
            const isWarn = attention?.level === 'warn';
            return (
              <Link
                key={d.path}
                to={d.path}
                data-testid={`floor-nav-${d.code.toLowerCase()}`}
                className={
                  'relative px-4 py-4 bg-[color:var(--t-bg-2)] transition-colors group block ' +
                  (active
                    ? 'ring-1 ring-inset ring-[color:var(--t-amber)]'
                    : isAlert
                      ? 'ring-1 ring-inset ring-[color:var(--t-amber)]/60 hover:bg-[color:var(--t-bg-3)]'
                      : 'hover:bg-[color:var(--t-bg-3)]')
                }
                style={
                  isAlert && !active
                    ? { boxShadow: 'inset 0 0 30px rgba(255,176,0,0.08)' }
                    : isWarn && !active
                      ? { boxShadow: 'inset 0 0 20px rgba(255,176,0,0.04)' }
                      : undefined
                }
              >
                <div className="flex items-center justify-between">
                  <Icon
                    className={
                      'w-3.5 h-3.5 ' +
                      (active ? 'text-[color:var(--t-amber)]' : 'text-[color:var(--t-text-2)]')
                    }
                  />
                  <span
                    className={
                      'font-mono text-[9px] tracking-[0.22em] ' +
                      (active ? 'text-[color:var(--t-amber)]' : 'text-[color:var(--t-text-3)]')
                    }
                  >
                    {d.code}
                  </span>
                </div>

                <div className="mt-4 flex items-end justify-between">
                  <div className="min-w-0">
                    <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[color:var(--t-text-3)]">
                      {d.sub}
                    </div>
                    <div
                      className={
                        'terminal-font font-bold text-[17px] mt-0.5 truncate ' +
                        (active ? 'text-[color:var(--t-amber)]' : 'text-[color:var(--t-text)]')
                      }
                    >
                      {d.label}
                    </div>
                  </div>
                  {active ? (
                    <span className="font-mono text-[9px] text-[color:var(--t-amber)] inline-flex items-center gap-1 shrink-0">
                      <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--t-amber)] animate-pulse" />
                      LIVE
                    </span>
                  ) : isAlert ? (
                    <span className="font-mono text-[9px] text-[color:var(--t-amber)] inline-flex items-center gap-1 shrink-0" data-testid={`floor-nav-attention-${d.key}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--t-amber)] animate-pulse" />
                      HOT
                    </span>
                  ) : (
                    <ArrowRight className="w-3.5 h-3.5 text-[color:var(--t-text-3)] shrink-0 group-hover:text-[color:var(--t-amber)] group-hover:translate-x-0.5 transition-all" />
                  )}
                </div>

                {/* Live metric strip */}
                <div className="mt-3 pt-3 border-t border-[color:var(--t-border)] flex items-center justify-between">
                  {metric ? (
                    <>
                      <span
                        key={`${d.key}-${flashKey}`}
                        className="num-flip font-mono text-[11px] tabular-nums text-[color:var(--t-amber)]"
                        data-testid={`floor-nav-metric-${d.key}`}
                      >
                        {metric.label}
                      </span>
                      <span className="font-mono text-[9px] text-[color:var(--t-text-3)] truncate ml-2">
                        {metric.unit}
                      </span>
                    </>
                  ) : (
                    <span className="font-mono text-[10px] text-[color:var(--t-text-3)]">&middot; &middot; &middot;</span>
                  )}
                </div>

                {/* Attention advisory line */}
                {attention && (
                  <div
                    className={
                      'mt-2 font-mono text-[10px] tracking-wide flex items-center gap-1.5 ' +
                      (isAlert ? 'text-[color:var(--t-amber)]' : 'text-[color:var(--t-amber)]/70')
                    }
                    data-testid={`floor-nav-advisory-${d.key}`}
                  >
                    <span className={isAlert ? 'w-1 h-1 rounded-full bg-[color:var(--t-amber)] animate-pulse' : 'w-1 h-1 rounded-full bg-[color:var(--t-amber)]/60'} />
                    {attention.message}
                  </div>
                )}
              </Link>
            );
          })}
        </div>

        <div className="mt-5 flex items-center justify-between font-mono text-[10px] text-[color:var(--t-text-3)] flex-wrap gap-2">
          <span>6 open desks &middot; base_sepolia &middot; protocol_online</span>
          <Link to="/why-getbitsy" className="hover:text-[color:var(--t-amber)] transition-colors">
            /why-getbitsy &rarr;
          </Link>
        </div>
      </div>
    </div>
  );
}
